import React, { useState } from 'react';
import { SportIcon } from '@/components/SportIcon';
import { ShieldCheck, Zap, Radio, MapPin, Clock, Award } from 'lucide-react';

export interface DynamicPlayerCardProps {
  name: string;
  handle: string;
  avatarId: string;
  sports: string[];
  tacticalRoles?: Record<string, string>;
  skillLevel: string;
  area: string;
  radiusKm: number;
  matchWindow?: string;
  isVerifiedAdult?: boolean;
  passId?: string;
  isCertified?: boolean;
  className?: string;
}

export const AVATAR_OPTIONS = [
  { id: 'striker', name: 'Striker', emoji: '⚽', color: '#3b82f6', bg: 'from-blue-600 to-indigo-700' },
  { id: 'blaster', name: 'Master Blaster', emoji: '🏏', color: '#10b981', bg: 'from-emerald-600 to-teal-700' },
  { id: 'smasher', name: 'Smasher', emoji: '🏸', color: '#8b5cf6', bg: 'from-purple-600 to-violet-700' },
  { id: 'baller', name: 'Hooper', emoji: '🏀', color: '#f59e0b', bg: 'from-amber-500 to-orange-600' },
  { id: 'court_ace', name: 'Court Ace', emoji: '🎾', color: '#ec4899', bg: 'from-pink-600 to-rose-700' },
  { id: 'dinker', name: 'Dink Master', emoji: '🏓', color: '#06b6d4', bg: 'from-cyan-600 to-blue-700' },
  { id: 'setter', name: 'Net Spiker', emoji: '🏐', color: '#6366f1', bg: 'from-indigo-600 to-blue-800' },
  { id: 'legend', name: 'All-Star', emoji: '⚡', color: '#eab308', bg: 'from-yellow-500 to-amber-600' },
];

export const SKILL_TIER_LABELS: Record<string, { label: string; badge: string; color: string }> = {
  beginner: { label: 'Rookie Enthusiast', badge: 'LEVEL 1', color: '#10b981' },
  casual: { label: 'Weekend Baller', badge: 'LEVEL 2', color: '#3b82f6' },
  intermediate: { label: 'Tactical Regular', badge: 'LEVEL 3', color: '#8b5cf6' },
  advanced: { label: 'Elite Competitor', badge: 'LEVEL 4', color: '#f59e0b' },
};

export default function DynamicPlayerCard({
  name,
  handle,
  avatarId,
  sports,
  tacticalRoles = {},
  skillLevel,
  area,
  radiusKm,
  matchWindow = 'Evening Floodlights (7 PM - 10 PM)',
  isVerifiedAdult = true,
  passId = 'KD-2026-X89',
  isCertified = false,
  className = '',
}: DynamicPlayerCardProps) {
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  const activeAvatar = AVATAR_OPTIONS.find((a) => a.id === avatarId) || AVATAR_OPTIONS[0];
  const tierInfo = SKILL_TIER_LABELS[skillLevel] || SKILL_TIER_LABELS.casual;
  const formattedHandle = handle ? (handle.startsWith('@') ? handle : `@${handle}`) : '@athlete';
  const displayName = name.trim() || 'KhelDrop Athlete';

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rX = ((y - centerY) / centerY) * -7;
    const rY = ((x - centerX) / centerX) * 7;
    setRotateX(rX);
    setRotateY(rY);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };

  return (
    <div
      style={{
        perspective: '1000px',
        width: '100%',
        maxWidth: '460px',
        margin: '0 auto',
      }}
      className={className}
    >
      <div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
          transition: 'transform 0.15s ease-out',
          transformStyle: 'preserve-3d',
          background: 'linear-gradient(135deg, #09090b 0%, #18181b 50%, #09090b 100%)',
          borderRadius: '24px',
          padding: '24px',
          color: '#ffffff',
          boxShadow: isCertified
            ? '0 20px 50px rgba(124, 58, 237, 0.35), 0 0 0 2px rgba(168, 85, 247, 0.6)'
            : '0 20px 45px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.12)',
          position: 'relative',
          overflow: 'hidden',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {/* Holographic Sheen / Background Grain */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(circle at 80% 20%, rgba(124, 58, 237, 0.22) 0%, transparent 50%), radial-gradient(circle at 10% 80%, rgba(59, 130, 246, 0.18) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        {/* Top Header Row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            paddingBottom: '14px',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.5)',
              }}
            >
              <Zap size={16} color="#ffffff" />
            </div>
            <div>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 900,
                  letterSpacing: '0.12em',
                  color: '#a1a1aa',
                  textTransform: 'uppercase',
                }}
              >
                KHELDROP PASSPORT
              </div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#f4f4f5', letterSpacing: '-0.02em' }}>
                ATHLETE ID CARD
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                padding: '3px 8px',
                borderRadius: '999px',
                fontSize: '10.5px',
                fontWeight: 800,
                color: '#34d399',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#10b981',
                  boxShadow: '0 0 6px #10b981',
                }}
              />
              RADAR ACTIVE
            </div>
            <div
              style={{
                fontSize: '10.5px',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: '#71717a',
                background: 'rgba(255, 255, 255, 0.06)',
                padding: '3px 7px',
                borderRadius: '6px',
              }}
            >
              {passId}
            </div>
          </div>
        </div>

        {/* Athlete Core Profile Details */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            position: 'relative',
            zIndex: 2,
            marginBottom: '16px',
          }}
        >
          {/* Avatar Disc */}
          <div
            style={{
              position: 'relative',
              width: '64px',
              height: '64px',
              borderRadius: '20px',
              background: `linear-gradient(135deg, ${activeAvatar.color}, #18181b)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              boxShadow: `0 8px 24px ${activeAvatar.color}55`,
              border: '2px solid rgba(255, 255, 255, 0.25)',
              flexShrink: 0,
            }}
          >
            {activeAvatar.emoji}
            <div
              style={{
                position: 'absolute',
                bottom: '-4px',
                right: '-4px',
                background: '#18181b',
                border: '1.5px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                padding: '1px 5px',
                fontSize: '9px',
                fontWeight: 900,
                color: tierInfo.color,
              }}
            >
              {tierInfo.badge}
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 900,
                color: '#ffffff',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#c084fc',
                letterSpacing: '-0.01em',
                marginTop: '2px',
              }}
            >
              {formattedHandle}
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                color: tierInfo.color,
                background: 'rgba(255, 255, 255, 0.06)',
                padding: '2px 8px',
                borderRadius: '6px',
                marginTop: '5px',
              }}
            >
              <Award size={12} />
              {tierInfo.label}
            </div>
          </div>
        </div>

        {/* Selected Sports & Tactical Roles */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            padding: '12px 14px',
            marginBottom: '14px',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#a1a1aa',
              marginBottom: '8px',
            }}
          >
            SPORT DNA & TACTICAL ROLES
          </div>

          {sports.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {sports.map((sp) => {
                  const role = tacticalRoles[sp.toLowerCase()] || tacticalRoles[sp];
                  return (
                    <div
                      key={sp}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '11.5px',
                        fontWeight: 700,
                        color: '#f4f4f5',
                      }}
                    >
                      <SportIcon sport={sp} size={14} color="#c084fc" />
                      <span>{sp}</span>
                      {role && (
                        <span
                          style={{
                            fontSize: '10px',
                            color: '#a1a1aa',
                            fontWeight: 600,
                            borderLeft: '1px solid rgba(255, 255, 255, 0.15)',
                            paddingLeft: '6px',
                          }}
                        >
                          {role}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '11px', color: '#71717a' }}>No sports selected yet</div>
          )}
        </div>

        {/* Location Hub & Match Radar Radius */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            marginBottom: '14px',
          }}
        >
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.07)',
              borderRadius: '12px',
              padding: '8px 10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#a1a1aa', fontSize: '10px', fontWeight: 800 }}>
              <MapPin size={11} color="#c084fc" />
              HOME RADAR
            </div>
            <div
              style={{
                fontSize: '11.5px',
                fontWeight: 700,
                color: '#ffffff',
                marginTop: '3px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {area || 'Delhi NCR'}
            </div>
            <div style={{ fontSize: '10px', color: '#a1a1aa', marginTop: '1px' }}>
              Radius: {radiusKm} km
            </div>
          </div>

          <div
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.07)',
              borderRadius: '12px',
              padding: '8px 10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#a1a1aa', fontSize: '10px', fontWeight: 800 }}>
              <Clock size={11} color="#f59e0b" />
              PREFERRED TIME
            </div>
            <div
              style={{
                fontSize: '11.5px',
                fontWeight: 700,
                color: '#ffffff',
                marginTop: '3px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {matchWindow.split('(')[0].trim() || 'Evenings'}
            </div>
            <div style={{ fontSize: '10px', color: '#a1a1aa', marginTop: '1px' }}>
              Pickup slots
            </div>
          </div>
        </div>

        {/* Footer: Legal 18+ DPDP Affirmation Seal */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            paddingTop: '10px',
            fontSize: '10.5px',
            color: '#a1a1aa',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <ShieldCheck size={14} color="#10b981" />
            <span style={{ fontWeight: 700, color: '#e4e4e7' }}>
              DPDP Act §9 & Contract Act Verified (18+)
            </span>
          </div>

          {isCertified && (
            <div
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #9333ea)',
                color: '#ffffff',
                fontSize: '9.5px',
                fontWeight: 900,
                padding: '2px 8px',
                borderRadius: '999px',
                letterSpacing: '0.04em',
                boxShadow: '0 0 10px rgba(147, 51, 234, 0.6)',
              }}
            >
              CERTIFIED PASS
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
